/**
 * How much room a value needs to be READ on a tile.
 *
 * A tile's header and audit line belong to the platform
 * (docs/platform-standards.md §5), so an arrangement describes only the body
 * between them. Its one say over the frame is how many rows it has: a second
 * or third row makes every tile in the list taller (item-cards.css). This
 * file is the editing model for that: it says how wide each data type has to
 * be, and the editor widens a field to its minimum as you drop it.
 *
 * keepr-api re-declares the same table in utils/cardLayouts/validate.js and
 * REFUSES anything narrower rather than silently widening it — the editor
 * widens, so a body that arrives narrow came from somewhere else. Change one,
 * change both.
 *
 * SIX COLUMNS since KPR-346 (owner rulings d04, d05): a tile is three rows of
 * six columns, and a body in sixths says `cols: 6`. The owner's migration
 * rewrote every body stored before then, and KPR-761 retired the reader of the
 * old width: every reader reads a body through `readTileBody`, which answers a
 * body in sixths and nothing else, so one that says no `cols: 6` is drawn as
 * a card with no tile layout.
 */

export const TILE_COLS = 6;
export const TILE_MAX_ROWS = 3;

/**
 * Long text: a field of four columns or more, its first line read under its
 * label. Alone in the body at all six it is the tile's 3-line paragraph
 * instead (resolveTileLayout) — the only reading it had before 2026-10-03,
 * when it had to be alone.
 */
export const LONG_TYPES = new Set(['text-large', 'rich-text']);

/**
 * Values that cannot be read in a third of the tile. A third is ~110px since
 * tiles became 390px wide, which holds a phone number and a person's avatar
 * and first name — so `phone` and `user` left this list then.
 */
export const WIDE_TYPES = new Set(['text-small', 'email', 'url', 'location', 'card-lookup']);

/**
 * The narrowest span a value of this type can still be read in, in sixths.
 * ⚑ KPR-346: every minimum is the three-column one doubled — a third of the
 * tile (2) for most, two thirds (4) for the wide and the long — so each type
 * keeps the room it was read in, and the new freedom is odd widths: a half,
 * five sixths. One sixth (~47px) holds neither a value nor, on a phone, a
 * field's two 44px controls; which types may take one is the owner's to rule.
 */
export const minSpanFor = (dataType) => {
	if (LONG_TYPES.has(dataType) || WIDE_TYPES.has(dataType)) return 4;
	return 2;
};

/** The span a field takes when it is dropped with room to spare: long text the whole row. */
export const defaultSpanFor = (dataType) => (LONG_TYPES.has(dataType) ? TILE_COLS : minSpanFor(dataType));

export const isLongType = (dataType) => LONG_TYPES.has(dataType);

/** What the editor says beside a field it will widen. */
export const describeSpan = (dataType) => {
	if (LONG_TYPES.has(dataType) || WIDE_TYPES.has(dataType)) return 'Needs 4 columns';
	return null;
};

/** Is this body written in sixths? Only `cols: 6` says so. */
export const isSixths = body => Boolean(body) && body.cols === TILE_COLS;

/**
 * A stored tile body, read in sixths: the body when it says `cols: 6`, else
 * `null`, which every caller reads as "this card has no tile layout" (the
 * automatic pick; an empty arrangement in the editor). A body in thirds (no
 * `cols`, or `cols: 3`) is never drawn at the wrong proportions and never
 * throws; none is stored any more (KPR-761), so this is a belt, not a reader.
 * The body is passed on as it is: anything in a body in sixths that is not a
 * placement is for the caller to drop, as it always was.
 */
export function readTileBody(body) {
	return isSixths(body) ? body : null;
}
